import React, { useState, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  Button,
  TextField,
  Spinner,
  Banner,
  EmptyState,
  InlineStack,
  Badge
} from '@shopify/polaris';
import { SearchIcon } from '@shopify/polaris-icons';
import { useNavigate } from 'react-router-dom';
import { useClient } from '../contexts/ClientContext';
import { useAuth } from '../contexts/AuthContext';

const TenantSelection: React.FC = () => {
  const { user } = useAuth();
  const { 
    clients, 
    loading, 
    error, 
    selectClient, 
    searchClients,
    retryFetchClients,
    hasFetched
  } = useClient();
  const navigate = useNavigate();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Fetch clients when component mounts
    if (!hasFetched && !loading && !error) {
      retryFetchClients();
    }
  }, [hasFetched, loading, error]); // Added error to dependencies to prevent retry on error

  // Redirect if client is already selected
  useEffect(() => {
    if (user?.role === 'support_manager' && clients.length > 0) {
      // Check if there's a stored client selection
      const storedClient = localStorage.getItem('selldo_ai_selected_client');
      if (storedClient) {
        try {
          const client = JSON.parse(storedClient);
          selectClient(client);
          navigate('/');
        } catch (error) {
          console.error('Error parsing stored client:', error);
          localStorage.removeItem('selldo_ai_selected_client');
        }
      }
    }
  }, [user?.role, clients, selectClient, navigate]);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    const timeout = setTimeout(() => {
      searchClients(value);
    }, 300);
    
    setSearchTimeout(timeout);
  };

  const handleClientSelect = (client: any) => {
    selectClient(client);
    // Redirect to dashboard after selecting a client
    navigate('/');
  };

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  // Only show for support managers
  if (user?.role !== 'support_manager') {
    return null;
  }

  return (
    <Page title="Select Client" fullWidth>
      <Layout>
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <div>
                <Text variant="headingMd" as="h2">
                  Choose a Client to Manage
                </Text>
                <Text variant="bodyMd" as="p" tone="subdued">
                  Select a client from the list below to access their data and settings.
                </Text>
              </div>

              {error && (
                <Banner tone="critical" onDismiss={() => {}}>
                  <Text as="p">{error}</Text>
                  <Button onClick={retryFetchClients} size="slim">
                    Retry
                  </Button>
                </Banner>
              )}

              <TextField
                label="Search clients"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search by client name..."
                prefix={<SearchIcon />}
                autoComplete="off"
              />

              {loading && (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <Spinner size="large" />
                  <Text variant="bodyMd" as="p">Loading clients...</Text>
                </div>
              )}

              {!loading && clients.length === 0 && hasFetched && (
                <EmptyState
                  heading="No clients found"
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                >
                  <Text as="p">
                    {searchQuery ? 'No clients match your search criteria.' : 'No clients are available at the moment.'}
                  </Text>
                  {searchQuery && (
                    <Button onClick={() => {
                      setSearchQuery('');
                      searchClients('');
                    }}>
                      Clear search
                    </Button>
                  )}
                </EmptyState>
              )}

              {!loading && clients.length > 0 && (
                <BlockStack gap="300">
                  <Text variant="bodyMd" as="p" fontWeight="semibold">
                    Available Clients ({clients.length})
                  </Text>
                  
                  <div style={{ display: 'grid', gap: '12px' }}>
                    {clients.map((client) => (
                      <Card key={client.id} padding="400">
                        <BlockStack gap="200">
                          <InlineStack align="space-between">
                            <div>
                              <Text variant="bodyMd" as="p" fontWeight="semibold">
                                {client.name}
                              </Text>
                              <Text variant="bodySm" as="p" tone="subdued">
                                Created: {new Date(client.created_at).toLocaleDateString()}
                              </Text>
                            </div>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                              {client.has_active_plan && (
                                <Badge tone="success">Active Plan</Badge>
                              )}
                              {client.available_credits !== undefined && (
                                <Badge tone="warning">
                                  {`${client.available_credits} Credits`}
                                </Badge>
                              )}
                            </div>
                          </InlineStack>
                          
                          <Button
                            variant="primary"
                            size="slim"
                            onClick={() => handleClientSelect(client)}
                          >
                            Select Client
                          </Button>
                        </BlockStack>
                      </Card>
                    ))}
                  </div>
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
};

export default TenantSelection;
